<template>
    <div class="container edit-container">
        <div class="row justify-content-center">
            <div class="col-12 case-settings-col-lg">
                <br />

                <!-- Empty Message -->
                <div v-show="publishers.length === 0 && !loading" class="text-center mt-4">
                    <br />
                    <br />
                    <h1 class="h5 font-weight-bolder mb-3">
                        <span class="icon icon-glyphicons-basic-education mr-2"></span>
                        <span>Publisher Profiles</span>
                    </h1>
                    <p class="mb-0 text-secondary">
                        Create a publisher profile for your school or institution to credit its contributions to the
                        community.
                    </p>
                    <br />
                    <button @click="openCreate()" type="button" class="btn btn-primary">Create a Publisher Profile</button>
                </div>

                <!-- Non-Empty Display -->
                <div v-show="publishers.length > 0 && !loading">
                    <div class="d-flex mb-3">
                        <div class="flex-grow-1 align-self-center">
                            <br />
                            <h1 class="h5 font-weight-bolder mb-2">
                                <span class="icon icon-glyphicons-basic-education mr-2"></span>
                                <span>Publisher Profiles</span>
                            </h1>
                            <p class="text-secondary mb-0">
                                Create a publisher profile for your school or institution to credit its contributions to
                                the community.
                            </p>
                        </div>
                        <div class="flex-shrink-0 align-self-end">
                            <button @click="openCreate()" type="button" class="btn btn-sm btn-primary ml-5">
                                Create a Publisher Profile
                            </button>
                        </div>
                    </div>
                </div>

                <!-- List Display -->
                <ul class="list-group">
                    <li v-for="publisher in publishers" :key="publisher.id" class="list-group-item">
                        <div class="d-flex">
                            <div class="flex-grow-1 align-self-center">
                                <div class="d-flex align-items-center mb-1">
                                    <div class="flex-grow-0 mr-3">
                                        <div
                                            class="
                                                list-logo
                                                rounded-circle
                                                d-flex
                                                align-items-center
                                                justify-content-center
                                            "
                                        >
                                            <img
                                                v-if="publisher.logo_file"
                                                :src="publisher.logo_file.signed_url"
                                                class="list-logo-img"
                                                alt="Publisher logo"
                                            />
                                        </div>
                                    </div>
                                    <div class="flex-grow-0">
                                        <p class="mb-0">
                                            {{ publisher.name }}
                                        </p>
                                    </div>
                                </div>
                                <p class="text-secondary small mb-1">Created on {{ formatDate(publisher.created) }}</p>
                            </div>
                            <div class="align-self-center">
                                <button @click="openRemove(publisher)" type="button" class="btn btn-light btn-sm mr-1">
                                    <span class="icon icon-glyphicons-basic-bin"></span>
                                </button>
                                <button @click="openAdmins(publisher)" class="btn btn-light btn-sm mr-1">Admins</button>
                                <button @click="openEdit(publisher)" class="btn btn-light btn-sm">
                                    <span class="icon icon-glyphicons-basic-square-edit"></span>
                                    Edit
                                </button>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Create/Edit Modal -->
        <base-modal id="edit-modal" :loading="modalLoading" :centered="true" size="large">
            <template slot="title">
                <span v-show="!selectedItem.id">Create Publisher</span>
                <span v-show="selectedItem.id">Edit Publisher</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p class="text-secondary">
                            This information will be displayed publicly on the community platform, linked to published
                            scenarios.
                        </p>
                        <base-form v-model="selectedItem" :fields="publisherFields" ref="publisherForm"></base-form>
                        <file-upload
                            v-model="selectedItem.logo_file"
                            :max-bytes="200000"
                            file-type-label="logo"
                            :direct-upload="true"
                            accept=".png|.jpg|.svg"
                        ></file-upload>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button v-show="!selectedItem.id" @click="create()" class="btn btn-primary" type="button">
                    Create
                </button>
                <button v-show="selectedItem.id" @click="update()" class="btn btn-primary" type="button">Save</button>
            </template>
        </base-modal>

        <!-- Remove Modal -->
        <base-modal id="remove-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Remove Publisher </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p class="mb-0">Remove publisher {{ selectedItem.name }}?</p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="remove()" class="btn btn-danger" type="button">Remove</button>
            </template>
        </base-modal>

        <!-- Admin Modal -->
        <base-modal id="admin-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Publisher Admins </template>
            <template>
                <p>Enter a team member's email to give them admin access to manage this publisher profile.</p>
                <div class="input-group mb-3">
                    <input v-model="newMemberEmail" type="email" class="form-control" placeholder="Email address" />
                    <div class="input-group-append">
                        <button class="btn btn-primary" type="button" @click="addAdmin()">Add</button>
                    </div>
                </div>
                <div v-for="member in admins" :key="member.id" class="mb-4 mt-4">
                    <div class="d-flex align-items-center">
                        <div class="flex-grow-1">
                            <span class="icon icon-glyphicons-basic-user mr-1"></span> {{ member.user.email }}
                        </div>
                        <div v-show="member.user.email !== currentUserEmail">
                            <button class="btn btn-gray btn-sm" @click="removeMember(member)">Remove</button>
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import Utilities from "@/assets/js/utilities.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import FileUpload from "@/components/FileUpload"

export default {
    name: "CommunityPublishersAdmin",
    components: {
        BaseModal,
        BaseForm,
        FileUpload,
    },
    data() {
        return {
            publishers: [],
            admins: [],
            modalLoading: false,
            selectedItem: {},
            newMemberEmail: "",
            publisherFields: [
                {
                    name: "Publisher Name",
                    key: "name",
                    subtext: "A name to identify your school or institution.",
                    placeholder: "Publisher Name",
                    maxlength: 150,
                    required: true,
                },
                {
                    name: "Website URL",
                    key: "website_url",
                    subtext: "The url of your organization's website.",
                    placeholder: "Website URL",
                    maxlength: 100,
                    required: true,
                },
                {
                    name: "Description",
                    key: "description",
                    type: "textarea",
                    subtext: "A description of your organization.",
                    placeholder: "Description",
                    maxlength: 1600,
                    required: true,
                    rows: 8,
                },
            ],
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        currentUserEmail() {
            return this.$store.getters.userEmail
        },
    },
    methods: {
        formatDate(date) {
            return Utilities.formatDateTime(date)
        },
        fetchData() {
            this.$store.dispatch("addLoad")
            let vm = this
            http.get("/api/community/publishers/")
                .then(function (response) {
                    vm.publishers = response.data
                })
                .finally(() => vm.$store.dispatch("removeLoad"))
        },
        fetchAdmins() {
            this.modalLoading = true
            let vm = this
            http.get(`/api/community/publishers/${this.selectedItem.id}/admins/`)
                .then(function (response) {
                    vm.admins = response.data
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        openCreate() {
            this.$refs.publisherForm.resetValidate()
            this.selectedItem = {}
            $("#edit-modal").modal("show")
        },
        openRemove(publisher) {
            this.selectedItem = publisher
            $("#remove-modal").modal("show")
        },
        openEdit(publisher) {
            this.$refs.publisherForm.resetValidate()
            this.selectedItem = publisher
            $("#edit-modal").modal("show")
        },
        openAdmins(publisher) {
            this.selectedItem = publisher
            this.fetchAdmins()
            $("#admin-modal").modal("show")
        },
        create() {
            if (!this.$refs.publisherForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post("/api/community/publishers/", this.selectedItem)
                .then(function () {
                    $("#edit-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Publisher created.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        update() {
            if (!this.$refs.publisherForm.validate()) {
                return
            }
            this.$store.dispatch("addLoad")
            let vm = this
            http.put(`/api/community/publishers/${this.selectedItem.id}/`, this.selectedItem)
                .then(function (response) {
                    vm.fetchData()
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Publisher saved.",
                        data: {},
                    })
                    $("#edit-modal").modal("hide")
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        remove() {
            this.modalLoading = true
            let vm = this
            http.put(`/api/community/publishers/${this.selectedItem.id}/remove/`)
                .then(function () {
                    $("#remove-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Publisher removed.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        addAdmin() {
            this.modalLoading = true
            let vm = this
            http.post(`/api/community/publishers/${this.selectedItem.id}/admins/`, {
                email: this.newMemberEmail,
            })
                .then(function () {
                    vm.fetchAdmins()
                    vm.newMemberEmail = ""
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Team member added.",
                        data: {},
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        removeMember(admin) {
            this.modalLoading = true
            let vm = this
            http.put(`/api/community/publisher-admins/${admin.id}/remove/`)
                .then(function () {})
                .finally(function () {
                    vm.modalLoading = false
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Team member removed.",
                        data: {},
                    })
                    vm.fetchAdmins()
                })
        },
    },
    mounted() {
        this.fetchData()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.list-logo {
    width: 40px;
    height: 40px;
    background-color: white;
    border: 1px solid $gray-200 !important;
    padding: 2px;
    overflow: hidden;
}

.list-logo-img {
    object-fit: cover;
    width: 100%;
}
</style>